import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Loader,
  Segment,
  Icon,
  Label,
  Pagination,
  Button,
  Dropdown,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PAGE_SIZE = 100;

const CONTENT_TYPE_ICONS = {
  document: 'file text',
  'source-code': 'code',
  presentation: 'desktop',
  specification: 'clipboard',
  research: 'lab',
  binary: 'file',
};

const SOURCE_DIR_LABELS = {
  docs: 'Documentation',
  heritage: 'Heritage',
  'source-archive': 'Source Archive',
  'nick-backend': 'Nick Backend',
};

/** Group items by month/year from effective_date */
function groupByMonth(items) {
  const groups = {};
  items.forEach((item) => {
    let key = 'Unknown date';
    if (item.effective_date) {
      const d = new Date(item.effective_date);
      if (!isNaN(d.getTime())) {
        const month = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        key = month;
      }
    }
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return groups;
}

const DocTimeline = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialProject = searchParams.get('project') || '';
  const initialSourceDir = searchParams.get('source_dir') || '';
  const initialContentType = searchParams.get('content_type') || '';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(initialProject);
  const [sourceDir, setSourceDir] = useState(initialSourceDir);
  const [contentType, setContentType] = useState(initialContentType);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (project) params.set('project', project);
    if (sourceDir) params.set('source_dir', sourceDir);
    if (contentType) params.set('content_type', contentType);
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@docs/timeline?${params.toString()}`)
      .then((json) => {
        setItems(json.items || []);
        setTotal(parseInt(json.items_total || 0, 10));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [project, sourceDir, contentType, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (project) params.set('project', project);
    if (sourceDir) params.set('source_dir', sourceDir);
    if (contentType) params.set('content_type', contentType);
    if (page > 1) params.set('page', page);
    const qs = params.toString();
    history.replace(`/docs/timeline${qs ? `?${qs}` : ''}`);
  }, [project, sourceDir, contentType, page, history]);

  const clearFilters = () => {
    setProject('');
    setSourceDir('');
    setContentType('');
    setPage(1);
  };

  const hasFilters = project || sourceDir || contentType;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const grouped = groupByMonth(items);

  return (
    <Container
      id="polyglot-docs-timeline"
      style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '900px' }}
    >
      <Helmet title="Timeline - LJUSA TIDER Knowledge Base" />
      <Link to="/docs">&larr; Back to Knowledge Base</Link>

      <div className="polyglot-page-header">
        <h1>Document Timeline</h1>
        <p className="subtitle">{total} documents ordered by date</p>
      </div>

      {/* Filters bar */}
      <Segment>
        <div style={{ display: 'flex', gap: '1em', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button.Group size="tiny">
            <Button as={Link} to="/docs">
              <Icon name="list" /> List
            </Button>
            <Button as={Link} to="/docs/timeline" active>
              <Icon name="clock" /> Timeline
            </Button>
          </Button.Group>

          {['docs', 'heritage', 'source-archive', 'nick-backend'].map((sd) => (
            <Label
              key={sd}
              as="a"
              size="small"
              color={sourceDir === sd ? 'blue' : undefined}
              basic={sourceDir !== sd}
              onClick={() => { setSourceDir(sourceDir === sd ? '' : sd); setPage(1); }}
            >
              {SOURCE_DIR_LABELS[sd] || sd}
            </Label>
          ))}

          {hasFilters && (
            <Button size="tiny" basic onClick={clearFilters}>
              <Icon name="close" /> Clear
            </Button>
          )}
        </div>
      </Segment>

      {error && <Segment color="red">Error: {error}</Segment>}

      {loading ? (
        <Loader active inline="centered" content="Loading timeline..." />
      ) : (
        <>
          {Object.entries(grouped).map(([month, monthItems]) => (
            <div key={month} style={{ marginBottom: '2em' }}>
              <Header as="h3" dividing style={{ color: '#555' }}>
                <Icon name="calendar" />
                {month}
                <Label size="tiny" circular>{monthItems.length}</Label>
              </Header>
              {monthItems.map((doc) => (
                <Segment
                  key={doc.id}
                  as={Link}
                  to={`/docs/${doc.slug}`}
                  style={{
                    display: 'block',
                    marginBottom: '0.5em',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                    <Icon
                      name={CONTENT_TYPE_ICONS[doc.content_type] || 'file'}
                      style={{ opacity: 0.6 }}
                    />
                    <strong>{doc.title}</strong>
                    {doc.project && (
                      <Label size="tiny" color="teal">{doc.project}</Label>
                    )}
                    {doc.source_dir && doc.source_dir !== 'docs' && (
                      <Label size="tiny" color="grey">
                        {SOURCE_DIR_LABELS[doc.source_dir] || doc.source_dir}
                      </Label>
                    )}
                    {doc.file_extension && (
                      <span style={{ color: '#999', fontSize: '0.85em' }}>
                        {doc.file_extension}
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', color: '#999', fontSize: '0.85em' }}>
                      {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                    </span>
                  </div>
                </Segment>
              ))}
            </div>
          ))}

          {items.length === 0 && (
            <Segment textAlign="center">No documents found.</Segment>
          )}

          {totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '1em' }}>
              <Pagination
                activePage={page}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => setPage(activePage)}
              />
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default DocTimeline;
